import {defaultHandlerCatch, defaultHandlerResolver, promisifyImage} from "./helpers";
import {photolabTask} from "../api";
import Processing from "../Processing";
import {mapAgeGroupToPromptString, normalizeAgeGroup} from "./d22.handler";
import clientStorage from "../../utils/client-storage";
import {BadResultError} from "../errors";
import {PhotolabTaskBuilder} from "@vicman-wi/photolab-api-js";

const tasksCache = {};

function photolabTaskHelper(creative, taskName, taskConfig, options) {
  const taskCacheKey = JSON.stringify(taskConfig);
  const _options = Object.assign({
    timeout: 1000,
    interval: 1000,
  }, options);

  if (!tasksCache[taskCacheKey]) {
    tasksCache[taskCacheKey] = photolabTask(taskConfig, {
      timeout: _options.timeout,
      interval: _options.interval,
    })
  }

  return tasksCache[taskCacheKey].then((taskResult) => {
    creative.setTask(taskName, taskResult);
    return taskResult;
  }).catch((err) => {
    delete tasksCache[taskCacheKey];
    throw err;
  });
}

async function roopTask(processing, creative, userFileUrl, promptResultFileUrl) {
  let targetFaceIndex = 0;
  const getFacesTaskResult = await photolabTaskHelper(creative, "roop_get_faces", new PhotolabTaskBuilder()
    .setLanguage(processing.language)
    .addImage(promptResultFileUrl)
    .addCollageMethod({template_name: "nn_roop", mode: "get_faces"})
    .build());

  if (getFacesTaskResult.humans?.items?.length > 1) {
    targetFaceIndex = getFacesTaskResult.humans.items
      .reduce((v, {bbox}, index) => {
        const square = (bbox.maxPoint.x - bbox.minPoint.x) * (bbox.maxPoint.y - bbox.minPoint.y);
        if (v.square < square) {
          return {index, square};
        } else {
          return v;
        }
      }, {index: 0, square: 0})
      .index;
  }

  return await photolabTaskHelper(creative, "roop", new PhotolabTaskBuilder()
    .setLanguage(processing.language)
    .addImage(userFileUrl)
    .addImage(promptResultFileUrl)
    .addCollageMethod({template_name: "nn_roop", target_face_index: targetFaceIndex})
    .build());
}

async function checkImageIsAdultness(processing, imageUrl) {
  const questionsResults = await Promise.all([
    photolabTask(new PhotolabTaskBuilder()
      .setLanguage(processing.language)
      .addCollageMethod({"@configId": "looklab_nsfw_questions"})
      .addImage(imageUrl)
      .build(), {timeout: 500,interval: 500})
      .then((result) => {
        return {
          is_nude: result.answers[0],
          is_naked: result.answers[1],
          naked_boobs: result.answers[2],
          has_nudity: result.answers[3],
          has_porno: result.answers[4],
          is_porno: result.answers[5],
          is_pornography: result.answers[6],
          porno: result.answers[7],
          naked_woman_boobs_visible: result.answers[8],
          has_explicit_nudity: result.answers[9],
        };
      }),
    photolabTask(new PhotolabTaskBuilder()
      .setLanguage(processing.language)
      .addCollageMethod({"@configId": "looklab_nsfw_questions_2"})
      .addImage(imageUrl)
      .build(), {timeout: 500,interval: 500})
      .then((result) => {
        return {
          has_naked_bottom: result.answers[0],
          has_sensual_lingerie: result.answers[1],
          has_erotic: result.answers[2],
          is_erotic: result.answers[3],
          seductive_woman_in_lingerie: result.answers[4],
          has_gun: result.answers[5],
          has_weapon: result.answers[6],
          has_gun_pointed_at_the_viewer: result.answers[7],
          is_this_a_child: result.answers[8],
          is_it_a_naked_child: result.answers[9],
          child_nudity: result.answers[10],
          is_it_a_child_in_a_swimsuit: result.answers[11],
          child_in_underwear: result.answers[12],
          child_erotic: result.answers[13],
        };
      }),
  ]);

  const answers = Object.assign({}, ...questionsResults);

  return {
    answers,
    isAdult: Object.values(answers).filter((a) => a.toLowerCase() !== "no").length > 3,
  };
}

/**
 * @param {Processing} processing
 * @param {Creative} creative
 */
export default (processing, creative) => {
  return new Promise(async (resolve, reject) => {
    try {
      const cropTaskResult = await photolabTaskHelper(creative, "crop", new PhotolabTaskBuilder()
        .addCollageMethod({template_name: 8987})
        .addImage(processing.file.url)
        .setLanguage(processing.language)
        .build());

      const {answers} = processing.getExtra(Processing.EXTRA_QUESTIONS_TASK);
      const gender = answers[0].split(" ").first();
      const ageGroup = normalizeAgeGroup(answers[3]);
      const seed = creative.getExtra("seed");

      const promptTaskResult = await photolabTaskHelper(creative, "prompt", new PhotolabTaskBuilder()
        .setLanguage(processing.language)
        .addCollageMethod({
          "@transformationId": "npp_base",
          "@configId": creative.getExtra("config_id"),
          "@replacements": [
            {
              parameters: ["prompt"],
              search: "{{gender}}",
              mode: "ig",
              replacement: gender,
            },
            {
              parameters: ["prompt"],
              search: "{{age_group}}",
              mode: "ig",
              replacement: mapAgeGroupToPromptString(ageGroup, gender),
            }
          ],
          param_sds: seed,
          gender,
        })
        .addImage(cropTaskResult.resultUrl)
        .setLanguage(processing.language)
        .build());

      if (creative.group.includes("users_")) {
        const check = await checkImageIsAdultness(processing, promptTaskResult.resultUrl);
        if (check.isAdult) {
          creative.setExtra("adult", check.isAdult);
          throw BadResultError.nsfw();
        }
      }

      let finalTaskResult = promptTaskResult;

      try {
        finalTaskResult = await roopTask(processing, creative, cropTaskResult.resultUrl, promptTaskResult.resultUrl);
      } catch (err) {
        console.log(err);
      }

      await promisifyImage(finalTaskResult.resultUrl);

      creative.setFile("raw", finalTaskResult.resultUrl);
      creative.markAsProcessed(finalTaskResult.resultUrl);

      if (!window.clientConfig.isPro && window.clientConfig.isCheapUser) {
        clientStorage.setD28TabIsProcessed();
      }

      defaultHandlerResolver(creative, resolve)();
    } catch (err) {
      defaultHandlerCatch(creative, reject)(err);
    }
  });
}